import { apiGet, apiPost, apiPatch, apiDelete } from '../utils/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FieldPlot {
  id: number;
  plot_code: string;
  name?: string;
  crop_type: string;
  farmer_id?: number;
  farmer_name?: string;
  primary_farmer_name?: string;
  farmers?: Array<{ id: number; name?: string; role_on_field?: string; is_primary?: boolean }>;
  farmer_count?: number;
  cohort_name?: string;
  assigned_agronomist_id?: number;
  assigned_agronomist_name?: string;
  assigned_field_facilitator_id?: number;
  assigned_field_facilitator_name?: string;
  expected_tree_count?: number;
  registered_tree_count?: number;
  mapped_tree_count?: number;
  grid_rows?: number;
  grid_columns?: number;
  center_lat?: number | string;
  center_lng?: number | string;
  area_hectares?: number | string;
  boundary_coordinates?: any;
  row_spacing_m?: number;
  tree_spacing_m?: number;
}

export interface TreeRecord {
  id: number;
  tree_code: string;
  crop_type: string;
  plot_id?: number;
  variety?: string;
  farmer_id?: number;
  farmer_name?: string;
  cohort_id?: number;
  cohort_name?: string;
  assigned_agronomist_id?: number;
  assigned_agronomist_name?: string;
  assigned_field_facilitator_id?: number;
  assigned_field_facilitator_name?: string;
  plot_code?: string;
  plot_name?: string;
  latitude?: number | string;
  longitude?: number | string;
  health_status: string;
  tree_status: string;
  production_status?: string;
  last_inspection_at?: string;
  open_issue_count?: number;
  latest_issue_photo?: string;
  latest_tree_photo?: string;
  observation_count?: number;
  latest_observation_at?: string;
  care_event_count?: number;
  latest_care_event_at?: string;
}

export interface Summary {
  total_trees: number;
  active_trees: number;
  mapped_trees: number;
  missing_gps: number;
  needs_inspection: number;
  farmers: number;
  cohorts: number;
  harvest_lots: number;
  traceable_kg: number;
  public_certificates: number;
  open_risks: number;
}

export interface GridPreviewPoint {
  latitude: number;
  longitude: number;
  row: number;
  column: number;
}

export interface GridPreview {
  points: GridPreviewPoint[];
  total_proposed: number;
  grid: {
    rows: number;
    columns: number;
    row_spacing_m: number;
    tree_spacing_m: number;
    bearing_degrees: number;
    placement_method: string;
  };
  validation: {
    boundary_valid: boolean;
    requested: number;
    proposed: number;
    inside_boundary: number;
    outside_boundary: number;
    duplicate_positions: number;
    all_inside_boundary: boolean;
    count_complete: boolean;
  };
  capacity: {
    available: number;
    max_capacity: number;
    area_ha: number;
    effective_area_ha: number;
    tree_area_m2: number;
    recommended_spacing: string | null;
  };
}

export interface GridPreviewResponse {
  success: boolean;
  field_id: number;
  field_code: string;
  field_name: string;
  requested_count: number;
  existing_tree_count: number;
  boundary_valid: boolean;
  preview: GridPreview;
}

export interface GenerateTreesResponse {
  success: boolean;
  field_id: number;
  field_code: string;
  field_name: string;
  requested_count: number;
  existing_before: number;
  created_count: number;
  mapped_count: number;
  inside_boundary_count: number;
  invalid_count: number;
  total_after: number;
  grid: any;
  validation: {
    boundary_valid: boolean;
    all_inside_boundary: boolean;
    codes_unique: boolean;
    count_complete: boolean;
  };
  summary: any;
  trees: TreeRecord[];
}

export interface RemapResponse {
  success: boolean;
  field_id: number;
  field_code: string;
  remapped_count: number;
  total_trees: number;
  grid: any;
  validation: {
    all_inside_boundary: boolean;
    inside_boundary: number;
    outside_boundary: number;
  };
}

export interface GridStatusResponse {
  success: boolean;
  field_id: number;
  field_code: string;
  expected: number;
  created: number;
  mapped: number;
  inside_boundary: number;
  invalid: number;
  total_after: number;
  is_aligned: boolean;
  status: string;
  has_boundary: boolean;
}

export interface TreeDetailResponse {
  success: boolean;
  tree: TreeRecord;
  observations: any[];
  careEvents: any[];
  timeline: any[];
  issues: any[];
  harvestLots: any[];
  evidence: any[];
  summary: any;
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

export async function fetchSummary(): Promise<Summary> {
  const data = await apiGet<any>('/api/traceability/summary');
  return data.summary;
}

export async function fetchFields(): Promise<FieldPlot[]> {
  const data = await apiGet<any>('/api/traceability/plots');
  return data.plots || [];
}

export async function fetchTrees(limit = 1000): Promise<TreeRecord[]> {
  const data = await apiGet<any>(`/api/traceability/trees?limit=${limit}`);
  return data.trees || [];
}

export async function searchFieldTrees(
  fieldPlotId: number,
  search = '',
  signal?: AbortSignal,
): Promise<{ trees: TreeRecord[]; total: number }> {
  const data = await apiGet<any>(
    '/api/traceability/trees',
    {
      plot_id: fieldPlotId,
      search: search.trim() || undefined,
      mapped_only: true,
      page: 1,
      limit: 100,
    },
    { signal, silent: true },
  );
  return {
    trees: Array.isArray(data.trees) ? data.trees : [],
    total: Number(data.pagination?.total || 0),
  };
}

export async function fetchTreeDetail(treeId: number): Promise<TreeDetailResponse> {
  return apiGet<TreeDetailResponse>(`/api/traceability/trees/${treeId}`);
}

export async function fetchGridStatus(fieldId: number): Promise<GridStatusResponse> {
  return apiGet<GridStatusResponse>(`/api/traceability/plots/${fieldId}/grid-status`);
}

export async function fetchGridPreview(
  fieldId: number,
  params: {
    tree_count: number;
    row_spacing_m?: number;
    tree_spacing_m?: number;
    bearing_degrees?: number;
    inner_margin_m?: number;
    boundary_coordinates?: string;
  }
): Promise<GridPreviewResponse> {
  return apiPost<GridPreviewResponse>(`/api/traceability/plots/${fieldId}/tree-grid/preview`, params);
}

export async function generateTrees(
  fieldId: number,
  params: {
    tree_count: number;
    crop_type?: string;
    variety?: string;
    row_spacing_m?: number;
    tree_spacing_m?: number;
    bearing_degrees?: number;
    inner_margin_m?: number;
    boundary_coordinates?: string;
    grid_rows?: number;
    grid_columns?: number;
    assigned_agronomist_id?: number;
    assigned_field_facilitator_id?: number;
    cohort_id?: number;
  }
): Promise<GenerateTreesResponse> {
  return apiPost<GenerateTreesResponse>(`/api/traceability/plots/${fieldId}/generate-trees`, params);
}

export async function clearFieldTrees(fieldId: number, confirmFieldCode: string | false = false): Promise<any> {
  if (confirmFieldCode === false) {
    return apiPost(`/api/traceability/plots/${fieldId}/clear-trees`, {});
  }
  return apiPost(`/api/traceability/plots/${fieldId}/clear-trees`, { confirm_field_code: confirmFieldCode });
}

export async function remapTreeGrid(
  fieldId: number,
  params: {
    row_spacing_m?: number;
    tree_spacing_m?: number;
    bearing_degrees?: number;
    inner_margin_m?: number;
  }
): Promise<RemapResponse> {
  return apiPost<RemapResponse>(`/api/traceability/plots/${fieldId}/tree-grid/remap`, params);
}

export async function updateField(fieldId: number, payload: Record<string, any>): Promise<any> {
  return apiPatch(`/api/traceability/plots/${fieldId}`, payload);
}

export async function deleteField(fieldId: number): Promise<any> {
  return apiDelete(`/api/traceability/plots/${fieldId}`);
}

export async function createField(payload: Record<string, any>): Promise<any> {
  return apiPost('/api/traceability/plots', payload);
}

export async function fetchHarvestLots(): Promise<any[]> {
  const data = await apiGet<any>('/api/traceability/harvest-lots');
  return data.harvestLots || [];
}

export async function fetchFarmers(): Promise<any[]> {
  const data = await apiGet<any>('/api/farmers');
  return data.farmers || data || [];
}

export async function fetchMapFarmers(): Promise<any[]> {
  try {
    return await apiGet<any[]>('/api/maps/farmers');
  } catch {
    return [];
  }
}

export async function fetchAgronomists(): Promise<any[]> {
  const data = await apiGet<any>('/api/users?role=agronomist');
  return data.users || data || [];
}
